import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Autocomplete, Avatar, Box, CircularProgress, TextField, Typography, } from "@mui/material";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useCreateOwnershipInvitation, useSearchOwnershipInvitableUsers, } from "../api/InvitationApiProvider";
import { useRetrieveTeam, useRetrieveUserTeamPermissions, } from "../ApiProvider";
import { ROUTES } from "../breadcrumbs/routeConfig";
import { ErrorView } from "../nav/Error";
import { Forbidden } from "../nav/Forbidden";
import { Loading } from "../nav/Loading";
import ErrorSnackbar from "../utils/ErrorSnackbar";
import { FormActions } from "../utils/FormActions";
import { useDebounce } from "../utils/useDebounce";
import { useNumericParams } from "../utils/useNumericParams";
export const SendOwnershipInvitation = () => {
    const { id: teamId } = useNumericParams();
    const navigate = useNavigate();
    const { data: permissions, isLoading: isPermLoading } = useRetrieveUserTeamPermissions({ teamId });
    const canModify = permissions?.canModify === true;
    const { data: team, isLoading: isTeamLoading, error: teamError, } = useRetrieveTeam({ id: teamId, enabled: canModify });
    const [errorMessage, setErrorMessage] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, 300);
    const { data: searchResults, isFetching: isSearchFetching } = useSearchOwnershipInvitableUsers({
        teamId,
        searchTerm: debouncedSearchTerm || undefined,
        enabled: canModify,
    });
    const { control, handleSubmit, watch, formState: { errors }, } = useForm({
        defaultValues: {
            invitee: null,
            email: "",
            message: "",
        },
    });
    const selectedInvitee = watch("invitee");
    const createMutation = useCreateOwnershipInvitation({
        teamId,
        onMutate: () => { },
        onSuccess: () => navigate(ROUTES.TEAM_OWNERSHIP_INVITATIONS.replace(":id", String(teamId))),
        onError: (error) => {
            const data = error?.response?.data;
            if (data) {
                const messages = Object.values(data).flat();
                setErrorMessage(messages.join(" "));
            }
            else {
                setErrorMessage("Failed to send ownership invitation.");
            }
        },
    });
    const onSubmit = (data) => {
        createMutation.mutate({
            ...(data.invitee ? { invitee: data.invitee } : {}),
            ...(data.email ? { email: data.email } : {}),
            ...(data.message ? { message: data.message } : {}),
        });
    };
    if (isPermLoading || (canModify && isTeamLoading)) {
        return _jsx(Loading, {});
    }
    if (!canModify) {
        return _jsx(Forbidden, {});
    }
    if (teamError || !team) {
        return _jsx(ErrorView, { error: teamError });
    }
    const playerOptions = (searchResults?.results || []).map((p) => ({
        id: p.userId,
        label: `${p.firstName} ${p.lastName}`,
        picture: p.picture,
    }));
    return (_jsxs(React.Fragment, { children: [_jsxs(Typography, { variant: "h4", gutterBottom: true, children: ["Transfer Ownership \u2014 ", team.name] }), _jsxs(Box, { component: "form", onSubmit: handleSubmit(onSubmit), noValidate: true, role: "form", sx: { maxWidth: 600, mt: 2 }, children: [_jsx(Typography, { variant: "subtitle2", color: "text.secondary", sx: { mb: 2 }, children: "Search for a registered user or enter an email address to invite someone to take ownership of this team." }), _jsx(Controller, { name: "invitee", control: control, rules: {
                            validate: (value) => {
                                const email = watch("email");
                                if (!value && !email) {
                                    return "Please select a user or enter an email address";
                                }
                                return true;
                            },
                        }, render: ({ field }) => (_jsx(Autocomplete, { options: playerOptions, getOptionLabel: (option) => typeof option === "string" ? option : option.label, isOptionEqualToValue: (option, value) => option.id === value.id, filterOptions: (x) => x, loading: isSearchFetching, onInputChange: (_e, value) => setSearchTerm(value), onChange: (_e, value) => {
                                field.onChange(value ? value.id : null);
                            }, renderOption: (props, option) => {
                                const { key, ...optionProps } = props;
                                return (_jsxs(Box, { component: "li", ...optionProps, sx: { display: "flex", alignItems: "center", gap: 1 }, children: [_jsx(Avatar, { src: option.picture ?? undefined, sx: { width: 32, height: 32 }, children: option.label.charAt(0) }), option.label] }, key));
                            }, renderInput: (params) => (_jsx(TextField, { ...params, label: "Search for User", margin: "normal", fullWidth: true, error: !!errors.invitee, helperText: errors.invitee?.message, InputProps: {
                                    ...params.InputProps,
                                    endAdornment: (_jsxs(_Fragment, { children: [isSearchFetching ? (_jsx(CircularProgress, { color: "inherit", size: 20 })) : null, params.InputProps.endAdornment] })),
                                } })) })) }), !selectedInvitee && (_jsx(Controller, { name: "email", control: control, rules: {
                            validate: (value) => {
                                const invitee = watch("invitee");
                                if (!invitee && !value) {
                                    return "Please select a user or enter an email address";
                                }
                                if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                                    return "Please enter a valid email address";
                                }
                                return true;
                            },
                        }, render: ({ field }) => (_jsx(TextField, { ...field, margin: "normal", fullWidth: true, label: "Or Enter Email Address", type: "email", error: !!errors.email, helperText: errors.email?.message })) })), _jsx(Controller, { name: "message", control: control, render: ({ field }) => (_jsx(TextField, { ...field, margin: "normal", fullWidth: true, label: "Message (Optional)", multiline: true, rows: 3 })) }), _jsx(FormActions, { submitLabel: "Send Invitation", isSubmitting: createMutation.isLoading })] }), _jsx(ErrorSnackbar, { open: !!errorMessage, onClose: () => setErrorMessage(""), message: errorMessage })] }));
};
